import {ChangeDetectionStrategy, Component, inject, signal, Signal, WritableSignal} from '@angular/core';
import {AppService} from '../../services/app/app.service';
import {AuthService} from '../../services/auth/auth.service';
import {HistoryService} from '../../services/history/history.service';
import {NavigationService} from '../../services/navigation/navigation.service';
import {AppQuery} from '../../queries/app.query';
import {CacheService} from '../../services/cache/cache.service';
import {TutorialService} from '../../services/tutorial/tutorial.service';
import {ListService} from '../../services/list/list.service';
import {TaskService} from '../../services/task/task.service';
import {LanguageService} from '../../services/language/language.service';
import {ServerSelectionService} from '../../services/server-selection/server-selection.service';
import {PermissionService} from '../../services/permission/permission.service';
import {CacheNameEnum} from '../../enums/cache/cache-name.enum';
import {Observable} from 'rxjs';
import {SEARCH_CACHE_MAX_AGE_IN_SECONDS} from '../../constants/cache/cache-max-age-in-seconds.constants';
import {environment} from '../../../environments/environment';
import {AmagnoLinkService} from '../../services/amagno-link/amagno-link.service';
import {AsyncPipe} from '@angular/common';
import {TokenService} from '../../services/token/token.service';
import {initialAppState} from '../../stores/app.store';
import {ThemeEnum} from '../../enums/theme.enum';
import {ThemeService} from '../../services/theme/theme.service';
import {TutorialQuery} from '../../queries/tutorial.query';
import {AppStorageService} from '../../services/app-storage/app-storage.service';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {AmagnoStorageKeyEnum} from '../../enums/amagno-storage-key.enum';
import {DocumentService} from '../../services/document/document.service';
import {ActivatedRouteSnapshot, Router} from '@angular/router';
import {StampService} from '../../services/stamp/stamp.service';

@Component({
    selector: 'app-debug-menu',
    templateUrl: './debug-menu.component.html',
    styleUrls: ['./debug-menu.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AsyncPipe]
})
export class DebugMenuComponent {
    protected open: WritableSignal<boolean>;
    protected deleteVaultWithoutPrompt$: Observable<boolean>;
    protected selectedMaxAgeTimeInSeconds: WritableSignal<number>;
    protected SEARCH_CACHE_MAX_AGE_IN_SECONDS = SEARCH_CACHE_MAX_AGE_IN_SECONDS;
    protected isVersionCheckActive: Signal<boolean>;
    protected readonly UserThemeEnum = ThemeEnum;
    protected isTutorialHidden$: Observable<boolean>;
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly appQuery = inject(AppQuery);
    private readonly appService = inject(AppService);
    private readonly cacheService = inject(CacheService);
    private readonly authService = inject(AuthService);
    private readonly historyService = inject(HistoryService);
    private readonly navigationService = inject(NavigationService);
    private readonly tutorialService = inject(TutorialService);
    private readonly listService = inject(ListService);
    private readonly taskService = inject(TaskService);
    private readonly languageService = inject(LanguageService);
    private readonly permissionService = inject(PermissionService);
    private readonly amagnoLinkService = inject(AmagnoLinkService);
    private readonly tokenService = inject(TokenService);
    private readonly themeService = inject(ThemeService);
    private readonly tutorialQuery = inject(TutorialQuery);
    private readonly appStorageService = inject(AppStorageService);
    private readonly documentService = inject(DocumentService);
    private readonly stampService = inject(StampService);
    private readonly router = inject(Router);

    public constructor() {
        this.open = signal(false);
        this.selectedMaxAgeTimeInSeconds = signal(SEARCH_CACHE_MAX_AGE_IN_SECONDS);
        this.isVersionCheckActive = this.serverSelectionService.getIsCheckingForVersionSignal();
        this.deleteVaultWithoutPrompt$ = this.appQuery.deleteVaultWithoutPrompt$;
        this.isTutorialHidden$ = this.tutorialQuery.isTutorialHidden$;
    }

    protected async trigger(action: string, data: string | number = ''): Promise<void> {
        const localstorageDebugKey = '__filling_localstorage__';
        if (action.includes('_prompt') || confirm('do action "' + action + '"?' + ((data) ? ' With data: ' + data : ''))) {
            switch (action) {
                case 'clear_app_version':
                    this.appService.setCurrentVersion('');
                    break;
                case 'set_theme': {
                    const theme = data as ThemeEnum;
                    if (Object.values(ThemeEnum)
                        .includes(theme)) {
                        this.themeService.setCurrentTheme(theme);
                    }
                    break;
                }
                case 'show_splashscreen':
                    this.appService.setShowSplashScreen(true, 0);
                    break;
                case 'hide_splashscreen':
                    this.appService.setShowSplashScreen(false, 0);
                    break;
                case 'clear_permission_cache':
                    this.permissionService.clearPermissions();
                    break;
                case 'clear_lists_cache':
                    this.listService.resetListStorage();
                    break;
                case 'clear_list_views':
                    this.appService.clearListViews();
                    break;
                case 'clear_panel_sizes':
                    this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO)
                        .delete(AmagnoStorageKeyEnum.PANELS);
                    break;
                case 'toggle_check_version':
                    this.serverSelectionService.toggleVersionCheck();
                    break;
                case 'set_is_disconnected':
                    this.appService.setIsDisconnected(true);
                    break;
                case 'invalidate_token':
                    this.tokenService.invalidateToken();
                    break;
                case 'invalidate_token_fake_data':
                    this.tokenService.invalidateToken({
                        token: 'test',
                        tokenExpiresTs: (Date.now()) + 2000,
                    });
                    break;
                case 'trigger_relog':
                    this.authService.tryToReAuthenticate()
                        .then();
                    break;
                case 'clear_history':
                    this.historyService.clearHistory();
                    break;
                case 'clear_tutorial_steps':
                    this.tutorialService.clearTutorialSteps()
                        .then();
                    break;
                case 'home':
                    this.navigationService.navigate([''])
                        .then();
                    break;
                case 'patterns':
                    this.navigationService.navigate(['patterns'])
                        .then();
                    break;
                case 'hide_menu':
                    this.appService.hideDebugMenu();
                    break;
                case 'show_list_loading':
                    this.listService.getLatestList()
                        .then(list => list?.setRefreshListToast(true));
                    break;
                case 'limit_image_loading_prompt': {
                    const currentMaxImagesPerWorker = this.appQuery.getMaxImagesPerWorker();
                    const promptMaxImagesPerWorker = await this.showPrompt('Amount of images', currentMaxImagesPerWorker + '');
                    if (promptMaxImagesPerWorker) {
                        const maxImagesPerWorker = Number.parseInt(promptMaxImagesPerWorker, 10);
                        this.appService.setMaxImagesPerWorker(maxImagesPerWorker);
                    }
                    break;
                }
                case 'set_language_prompt': {
                    const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
                    const promptCultureName = await this.showPrompt('Culture Name (de-DE)', currentIntlLocale.baseName);
                    if (promptCultureName) {
                        this.languageService.setCurrentIntlLocale(new Intl.Locale(promptCultureName));
                    }
                    break;
                }
                case 'clear_localstorage_full':
                    try {
                        localStorage.clear();
                        window.location.reload();
                    } catch (error) {
                        console.error('Failed to clear localStorage', error);
                    }
                    break;
                case 'logout':
                    this.authService.logout()
                        .then(() => {
                            this.navigationService.navigateToLogin()
                                .then();
                        });
                    break;
                case 'logout_with_azure':
                    this.authService.logout(true)
                        .then(() => {
                            this.navigationService.navigateToLogin()
                                .then();
                        });
                    break;
                case 'logout_azure': {
                    const currentLocation = window.location.href;
                    window.location.href = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=' + encodeURI(currentLocation);
                    break;
                }
                case 'fetch_user_tasks':
                    this.taskService.resetTimer();
                    break;
                case 'toggle_delete_vault_without_prompt':
                    this.appService.toggleDeleteVaultWithoutPrompt();
                    break;
                case 'set_search_cache_max_age': {
                    const maxAgeTimeInSeconds = Number(data);
                    this.cacheService.setMaxAgeInSecondsByCacheName(CacheNameEnum.Search, maxAgeTimeInSeconds);
                    this.selectedMaxAgeTimeInSeconds.set(maxAgeTimeInSeconds);
                    break;
                }
                case 'fake_deep_link_prompt': {
                    const promptDeepLink = await this.showPrompt(environment.deepLinkPrefix + 'abcdefgh.....');
                    if (promptDeepLink) {
                        this.amagnoLinkService.openAmagnoLinkByAmagnoDeepLink(promptDeepLink)
                            .then();
                    }
                    break;
                }
                case 'set_toast_delay_prompt': {
                    const currentToastDelayInMs = this.appQuery.getToastDelayMS();
                    const promptSetToastDelayInMs = await this.showPrompt('Set toast delay in ms', currentToastDelayInMs + '');
                    if (promptSetToastDelayInMs) {
                        const toastDelayInMs = Number.parseInt(promptSetToastDelayInMs, 10);
                        if (toastDelayInMs > 0) {
                            this.appService.setToastDelayInMS(toastDelayInMs);
                        }
                    }
                    break;
                }
                case 'fill_debug_localstorage': {
                    localStorage.removeItem(localstorageDebugKey);
                    let max = 1; // This holds the highest order bit.
                    let localstorageData = 'x'; // The string we're going to store in localStorage.

                    // Code-Source: https://mmazzarolo.com/blog/2022-06-26-filling-local-storage-programmatically/
                    // Find the highest order bit.
                    try {
                        // noinspection InfiniteLoopJS
                        while (true) {
                            localstorageData = localstorageData + localstorageData;
                            localStorage.vanillaSetItem(localstorageDebugKey, localstorageData);
                            max <<= 1;
                        }
                    } catch {
                        // Do nothing
                    }

                    // Fill the rest of the space by increasing the string length in the opposite
                    // order.
                    for (let bit = max >> 1; bit > 0; bit >>= 1) {
                        try {
                            localStorage.vanillaSetItem(localstorageDebugKey, localstorageData.substring(0, max | bit));
                            max |= bit;
                        } catch {
                            // Do nothing
                        }
                    }
                    break;
                }
                case 'empty_debug_localstorage':
                    localStorage.removeItem(localstorageDebugKey);
                    break;
                case 'corrupt_localstorage_truncate':
                    this.corruptAllLocalStorageValues(value => value.substring(0, Math.floor(value.length / 2)));
                    break;
                case 'corrupt_localstorage_garbage':
                    this.corruptAllLocalStorageValues(() => '{"corrupted":\u0000�');
                    break;
                case 'corrupt_localstorage_outdated_server_data':
                    try {
                        localStorage.vanillaSetItem('aptera', '{"app":');
                        window.location.reload();
                    } catch (error) {
                        console.error('Failed to corrupt the outdated server data', error);
                    }
                    break;
                case 'reset_toast_delay':
                    this.appService.setToastDelayInMS(initialAppState.toastDelayMS);
                    break;
                case 'toggle_hide_tutorial':
                    this.tutorialService.toggleHideTutorial();
                    break;
                case 'mark_selected_document_changed': {
                    const changedDocumentId = this.getSelectedDocumentIdFromUrl();
                    if (changedDocumentId) {
                        await this.listService.markItem(changedDocumentId);
                        this.stampService.stampAddedToDocument.emit(changedDocumentId);
                    }
                    break;
                }
                case 'mark_selected_document_deleted': {
                    const deletedDocumentId = this.getSelectedDocumentIdFromUrl();
                    if (deletedDocumentId) {
                        await this.listService.deleteItem(deletedDocumentId);
                        this.documentService.documentDeletedEvent.emit(deletedDocumentId);
                    }
                    break;
                }
                default:
            }
            this.closeMenu();
        }
    }

    protected toggleMenu(): void {
        this.open.update(open => !open);
    }

    private closeMenu(): void {
        this.open.set(false);
    }

    private corruptAllLocalStorageValues(createCorruptedValue: (value: string) => string): void {
        try {
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                localStorage.vanillaSetItem(key, createCorruptedValue(localStorage.getItem(key) ?? ''));
            }
            window.location.reload();
        } catch (error) {
            console.error('Failed to corrupt localStorage', error);
        }
    }

    private getSelectedDocumentIdFromUrl(): string | undefined {
        let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
        while (route) {
            const documentId = route.paramMap.get('documentId') ?? route.queryParamMap.get('documentId');
            if (documentId) {
                return documentId;
            }
            route = route.firstChild;
        }
        return undefined;
    }

    private showPrompt(message: string, defaultValue: string = ''): Promise<string | null> {
        return new Promise<string | null>((resolve) => {
            const dialog = document.createElement('dialog');
            const form = document.createElement('form');
            form.method = 'dialog';
            form.style.cssText = 'display:flex;flex-direction:column;gap:8px;min-width:300px;padding:8px';

            const label = document.createElement('label');
            label.textContent = message;

            const input = document.createElement('input');
            input.style.cssText = 'padding:4px';
            input.type = 'text';
            input.value = defaultValue;
            input.dataset.qa = 'debug-prompt-input';
            input.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    dialog.close('ok');
                }
            });

            const buttonRow = document.createElement('div');
            buttonRow.style.cssText = 'display:flex;gap:8px;justify-content:flex-end';

            const cancelButton = document.createElement('button');
            cancelButton.textContent = 'Cancel';
            cancelButton.type = 'button';
            cancelButton.dataset.qa = 'debug-prompt-cancel-button';
            cancelButton.addEventListener('click', () => {
                dialog.close('cancel');
            });

            const okButton = document.createElement('button');
            okButton.textContent = 'OK';
            okButton.type = 'button';
            okButton.dataset.qa = 'debug-prompt-ok-button';
            okButton.addEventListener('click', () => {
                dialog.close('ok');
            });

            buttonRow.appendChild(cancelButton);
            buttonRow.appendChild(okButton);
            form.appendChild(label);
            form.appendChild(input);
            form.appendChild(buttonRow);
            dialog.appendChild(form);

            dialog.addEventListener('close', () => {
                const value = dialog.returnValue === 'ok' ? input.value : null;
                dialog.remove();
                resolve(value);
            });

            document.body.appendChild(dialog);
            dialog.showModal();
            input.focus();
            input.select();
        });
    }
}
